import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { SitePicker } from '../SitePicker.js'
import { Label } from './panes.js'
import type { Job } from './types.js'

/**
 * A job with nowhere to go, and the way to fix it.
 *
 * There are a lot of these, because ServiceM8 kept the address on the job and
 * the import only links a site when it can match one. Naming the property is
 * done here rather than by editing the client record and coming back.
 *
 * There is no "no particular site" option: the job is already in that state,
 * and offering it as a choice would be offering to do nothing.
 */
export function NoSitePanel({ job }: { job: Job }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()

  const setSite = useMutation(
    trpc.jobs.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        void queryClient.invalidateQueries(trpc.schedule.pathFilter())
      },
    }),
  )

  return (
    <div className="flex flex-col gap-3">
      <Label>Site</Label>
      <p className="text-[13px] text-ink-500">
        This job names no property. ServiceM8 kept the address on the job itself, so an imported
        job with a one-off address has nothing to link to.
      </p>

      {can('JOB_EDIT') ? (
        <>
          <SitePicker
            id="job-set-site"
            clientId={job.client.id}
            value=""
            allowNone={false}
            onChange={(siteId) => {
              if (!siteId) return
              setSite.mutate({ id: job.id, siteId })
            }}
          />
          {setSite.error ? (
            <p className="text-xs text-red-600" role="alert">
              {errorMessage(setSite.error)}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
